import { memo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  isoToDatetimeLocal,
  datetimeLocalToIso,
} from "@/shared/lib/date-format";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { applyFieldErrorsToForm } from "@/shared/lib/form-error";
import { RATE_TYPE_LABELS } from "./CommissionTypeBadge";
import { GraduationAsyncSelect } from "./GraduationAsyncSelect";
import { RentalAsyncSelect } from "./RentalAsyncSelect";
import { SellerAsyncSelect } from "./SellerAsyncSelect";
import type {
  CommissionResponseDTO,
  CommissionUpsertRequestDTO,
  RateType,
} from "../types/commission.types";

const SELECT_CLASSES =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

const commissionFormSchema = z
  .object({
    sellerId: z
      .number({ message: "Vendedor é obrigatório" })
      .int()
      .positive("Vendedor é obrigatório"),
    origin: z.enum(["RENTAL", "GRADUATION"]),
    referenceId: z
      .number({ message: "ID da origem é obrigatório" })
      .int()
      .positive("ID deve ser maior que zero"),
    rateType: z.enum(["SUIT", "SHOES", "GOWN", "GRADUATION"]),
    value: z
      .number({ message: "Valor é obrigatório" })
      .min(0, "Valor não pode ser negativo"),
    calculatedAt: z.string().min(1, "Data de cálculo é obrigatória"),
    rentedAt: z.string().min(1, "Data de origem é obrigatória"),
  })
  .superRefine((data, ctx) => {
    if (data.rateType === "GRADUATION" && data.origin !== "GRADUATION") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["origin"],
        message: "Comissões do tipo Formatura exigem origem Formatura",
      });
    }
    if (data.rateType !== "GRADUATION" && data.origin === "GRADUATION") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["origin"],
        message: "Origem Formatura só aceita comissões do tipo Formatura",
      });
    }
  });

export type CommissionFormData = z.infer<typeof commissionFormSchema>;

interface CommissionFormDialogProps {
  open: boolean;
  loading: boolean;
  /**
   * Se fornecido, o vendedor fica travado (modal de detalhes).
   * Se `null`, o usuário escolhe via `SellerAsyncSelect` (criação a
   * partir da tela principal).
   */
  lockedSellerId: number | null;
  /** Nome exibido quando `lockedSellerId` está preenchido. */
  lockedSellerName?: string;
  commission: CommissionResponseDTO | null;
  onClose: () => void;
  onSubmit: (data: CommissionUpsertRequestDTO) => Promise<void>;
}

const EMPTY_DEFAULTS: CommissionFormData = {
  sellerId: 0,
  origin: "RENTAL",
  referenceId: 0,
  rateType: "SUIT",
  value: 0,
  calculatedAt: "",
  rentedAt: "",
};

function buildDefaults(
  lockedSellerId: number | null,
  commission: CommissionResponseDTO | null,
): CommissionFormData {
  if (commission) {
    const isGraduation = commission.graduationId !== null;
    return {
      sellerId: commission.sellerId,
      origin: isGraduation ? "GRADUATION" : "RENTAL",
      referenceId: isGraduation
        ? (commission.graduationId ?? 0)
        : (commission.rentalId ?? 0),
      rateType: commission.rateType,
      value: commission.value,
      calculatedAt: isoToDatetimeLocal(commission.calculatedAt),
      rentedAt: isoToDatetimeLocal(commission.rentedAt),
    };
  }
  return {
    ...EMPTY_DEFAULTS,
    sellerId: lockedSellerId ?? 0,
  };
}

export const CommissionFormDialog = memo(function CommissionFormDialog({
  open,
  loading,
  lockedSellerId,
  lockedSellerName,
  commission,
  onClose,
  onSubmit,
}: CommissionFormDialogProps) {
  const form = useForm<CommissionFormData>({
    resolver: zodResolver(commissionFormSchema),
    defaultValues: EMPTY_DEFAULTS,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(buildDefaults(lockedSellerId, commission));
  }, [open, lockedSellerId, commission, form]);

  const isEdit = commission !== null;

  const handleValid = async (data: CommissionFormData) => {
    const isGraduation = data.origin === "GRADUATION";
    const payload: CommissionUpsertRequestDTO = {
      sellerId: data.sellerId,
      rentalId: isGraduation ? null : data.referenceId,
      graduationId: isGraduation ? data.referenceId : null,
      rateType: data.rateType,
      value: data.value,
      calculatedAt: datetimeLocalToIso(data.calculatedAt),
      rentedAt: datetimeLocalToIso(data.rentedAt),
    };
    try {
      await onSubmit(payload);
    } catch (error: unknown) {
      if (applyFieldErrorsToForm(error, form)) return;
      toast.error(
        getApiErrorMessage(
          error,
          isEdit ? "Erro ao atualizar comissão" : "Erro ao criar comissão",
        ),
      );
    }
  };

  const origin = form.watch("origin");
  const sellerLocked = lockedSellerId !== null;

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Editar Comissão" : "Nova Comissão"}
          </DialogTitle>
          <DialogDescription>
            {sellerLocked && lockedSellerName ? (
              <>
                Vendedor:{" "}
                <span className="font-medium">{lockedSellerName}</span>
                {isEdit && commission ? ` · Comissão #${commission.id}` : ""}
              </>
            ) : isEdit && commission ? (
              <>Comissão #{commission.id}</>
            ) : (
              "Selecione o vendedor e preencha os dados da comissão."
            )}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleValid)} className="space-y-4">
            {!sellerLocked && (
              <FormField
                control={form.control}
                name="sellerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vendedor *</FormLabel>
                    <FormControl>
                      <SellerAsyncSelect
                        inputId="commission-form-seller"
                        value={field.value > 0 ? field.value : null}
                        onChange={(id) => field.onChange(id ?? 0)}
                        disabled={loading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="origin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Origem *</FormLabel>
                  <FormControl>
                    <div
                      className="flex gap-4"
                      role="radiogroup"
                      aria-label="Origem da comissão"
                    >
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name={field.name}
                          value="RENTAL"
                          checked={field.value === "RENTAL"}
                          onChange={() => {
                            field.onChange("RENTAL");
                            form.setValue("referenceId", 0);
                            if (form.getValues("rateType") === "GRADUATION") {
                              form.setValue("rateType", "SUIT");
                            }
                          }}
                          disabled={loading}
                        />
                        Aluguel
                      </label>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="radio"
                          name={field.name}
                          value="GRADUATION"
                          checked={field.value === "GRADUATION"}
                          onChange={() => {
                            field.onChange("GRADUATION");
                            form.setValue("referenceId", 0);
                            form.setValue("rateType", "GRADUATION");
                          }}
                          disabled={loading}
                        />
                        Formatura
                      </label>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="referenceId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {origin === "GRADUATION" ? "Formatura *" : "Aluguel *"}
                  </FormLabel>
                  <FormControl>
                    {origin === "GRADUATION" ? (
                      <GraduationAsyncSelect
                        inputId="commission-form-graduation"
                        value={field.value > 0 ? field.value : null}
                        onChange={(id) => field.onChange(id ?? 0)}
                        disabled={loading}
                      />
                    ) : (
                      <RentalAsyncSelect
                        inputId="commission-form-rental"
                        value={field.value > 0 ? field.value : null}
                        onChange={(id) => field.onChange(id ?? 0)}
                        disabled={loading}
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="rateType"
                render={({ field }) => {
                  const allowedTypes: RateType[] =
                    origin === "GRADUATION"
                      ? ["GRADUATION"]
                      : ["SUIT", "SHOES", "GOWN"];
                  return (
                    <FormItem>
                      <FormLabel>Tipo *</FormLabel>
                      <FormControl>
                        <select
                          {...field}
                          className={SELECT_CLASSES}
                          disabled={loading || allowedTypes.length === 1}
                        >
                          {allowedTypes.map((type) => (
                            <option key={type} value={type}>
                              {RATE_TYPE_LABELS[type]}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valor (R$) *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={field.value || ""}
                        placeholder="0,00"
                        onChange={(e) => {
                          const raw = e.target.value;
                          field.onChange(raw === "" ? 0 : Number(raw));
                        }}
                        disabled={loading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="calculatedAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data de Cálculo *</FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        {...field}
                        disabled={loading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="rentedAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data de Origem *</FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        {...field}
                        disabled={loading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading
                  ? "Salvando..."
                  : isEdit
                    ? "Salvar Alterações"
                    : "Criar Comissão"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
